import { useState, type FC } from "react";
import SecondaryPageTitleSection from "../../molecules/SecondaryPageTitleSection";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { Col, Row } from "react-grid-system";
import ListTabBar from "../../molecules/ListTabBar";
import DeleteInvoicesButton from "../../molecules/invoices/DeleteInvoicesButton";
import SendInvoiceMessageToClientButton from "../../molecules/invoices/SendInvoiceMessageToClientButton";
import Card from "../../atoms/Card";
import InvoiceViewColumn from "../../organisms/validation/InvoiceViewCoulmn";
import { useInvoiceOverviewDetails } from "../../../graphql/hooks/invoices/useInvoiceOverviewDetails";
import Subheading from "../../atoms/typography/Subheading";
import { theme } from "../../../theme";
import BodySmall from "../../atoms/typography/BodySmall";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import Body from "../../atoms/typography/Body";
import { formatPrice } from "../../../utils/formatters";
import dayjs from "dayjs";
import Alert from "../../molecules/Alert";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import {
  getInvoiceDistributionTypeTranslationKey,
  getInvoiceDistrubitionTypeRecipientAddress,
  getInvoiceLedgerStateBadge,
  getInvoiceListStatusBadge,
  getInvoiceOverdueStatusBadge,
  getInvoiceServiceTypeTranslationKey,
} from "../../../utils/invoicesHelpers";
import styled from "styled-components";
import { getDocumentUploadSource } from "../../../utils/documentHandlerHelpers";
import Small from "../../atoms/typography/Small";
import T800Result from "../../molecules/assessment/T800Result";
import InvoiceValidationResult from "../../molecules/invoices/InvoiceValidationResult";
import DownloadButton from "../../atoms/buttons/DownloadButton";
import { useInvoiceDeliveryApprovals } from "../../../graphql/hooks/invoices/useInvoiceDeliveryApprovals";
import { LgHistoryDetailBlock } from "../../molecules/assessment/DeliverApprovalStatusOnInvoice";
import TextTag from "../../atoms/TextTag";
import TextButton from "../../atoms/buttons/TextButton";
import { InvoicesDoneRoute } from "../../../utils/routes";
import type { UserModel } from "../../../models/UserModel";
import CopyForDebuggingButton from "../../molecules/CopyForDebuggingButton";

const tabs: Array<{
  tabLabelKey?: string;
  tabLabel?: React.ReactNode;
  id: any;
  color?: string;
}> = [
  {
    tabLabelKey: "Detaljer",
    id: "done",
  },
  {
    tabLabelKey: "Bedömning",
    id: "assessment",
  },
  {
    tabLabelKey: "Validering",
    id: "validation",
  },
];

const GreyOutlinedContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DataRow = styled.div<{ border?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${(props) => (props.border ? "8px" : "0px")};
  border-bottom: ${(props) =>
    props.border ? `1px solid ${theme.colors.grey05}` : "none"};
  align-items: center;
`;

const formatUser = (user?: UserModel) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  return name.length > 0 ? name : "Okänt";
};

const InvoiceOverviewPage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id, clientIdentifier } = useParams();
  const navigate = useNavigate();
  const from = location.state?.from || "/";

  const [selectedTabId, setSelectedTabId] = useState<string>("done");

  const { invoice, error, loading } = useInvoiceOverviewDetails(
    clientIdentifier,
    id,
  );
  const { invoice: invoiceDeliveryApprovals } = useInvoiceDeliveryApprovals(
    clientIdentifier,
    id,
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  const displayInvoceStateButtonNavigations =
    invoice?.state?.processStatus === "DONE" ||
    invoice?.state?.processStatus === "REJECTED";

  const onClickOpenInValidation = () => {
    navigate(`/invoices/validate/${id}/${clientIdentifier}`, {
      state: { from: InvoicesDoneRoute.id },
    });
  };
  const onClickOpenInAssessment = () => {
    navigate(
      `/invoices/assess/${clientIdentifier}/${id}?displayAssessedInvoice=${id}`,
      {
        state: { from: InvoicesDoneRoute.id },
      },
    );
  };

  return (
    <div>
      <SecondaryPageTitleSection
        from={from}
        titleKey={invoice?.invoiceNumber ?? "invoices.invoice"}
        currentTitleKey="invoices.invoice"
      />
      <HorizontalSpacer spacing={2} />

      {error && invoice?.state?.processStatus !== "REGISTRATION" && (
        <Alert
          type="error"
          title={t("errors.couldNotFetchInvoice")}
          message={error?.message}
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 16,
        }}
      >
        {displayInvoceStateButtonNavigations && (
          <TextButton
            title={t("invoices.displayInValidation")}
            onClick={onClickOpenInValidation}
            iconPosition="right"
            as="a"
          />
        )}
        {displayInvoceStateButtonNavigations && (
          <TextButton
            title={t("invoices.displayInAssessment")}
            onClick={onClickOpenInAssessment}
            iconPosition="right"
            as="a"
          />
        )}
        {id && (
          <DeleteInvoicesButton
            buttonType="outlinedSmallDiscrete"
            invoiceIds={[id]}
          />
        )}
        {id && (
          <SendInvoiceMessageToClientButton
            invoiceIds={[id]}
            clientIdentifier={clientIdentifier ?? ""}
            buttonType="outlinedSmallDiscrete"
          />
        )}
      </div>

      <HorizontalSpacer spacing={2} />
      <Card spacing={1.5}>
        <Row gutterWidth={16}>
          <Col lg={7}>
            <Row nogutter align="center" justify="between">
              <Subheading color={theme.colors.primary}>Översikt</Subheading>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {invoice?.state?.processStatus === "REGISTRATION" &&
                  invoice?.state?.registrationStatus === "AUTOPILOT_PAUSE" && (
                    <TextTag variant="info" tagTitleKey="Ej överlåten" />
                  )}
                {invoice?.state?.registrationStatus === "MIGRATED" && (
                  <TextTag tagTitleKey="Migrerad" variant="green" small />
                )}
                {invoice?.state?.processStatus &&
                  getInvoiceListStatusBadge(
                    invoice?.state?.processStatus,
                    false,
                  )}
                {invoice?.isCreditInvoice && (
                  <TextTag tagTitleKey="Kreditfaktura" variant="pink" />
                )}
              </div>
            </Row>
            <HorizontalSpacer spacing={1} />
            <Row>
              <Col lg={4}>
                <BodySmall color={theme.colors.textSecondary}>
                  Utställare
                </BodySmall>
                <TextLinkButton
                  title={invoice?.invoiceIssuer?.client?.name ?? "Okänt"}
                  onClick={() =>
                    navigate(`/client/${invoice?.invoiceIssuer?.client?.id}`)
                  }
                  disabled={!invoice?.invoiceIssuer?.client?.id}
                />
                <Small>{invoice?.invoiceIssuer?.client?.id}</Small>
              </Col>
              <Col lg={4}>
                <BodySmall color={theme.colors.textSecondary}>
                  Mottagare
                </BodySmall>
                <TextLinkButton
                  title={invoice?.invoiceRecipient?.name ?? "Okänt"}
                  onClick={() =>
                    navigate(`/client/${invoice?.invoiceRecipient?.client?.id}`)
                  }
                  disabled={!invoice?.invoiceRecipient?.client?.id}
                />
                <Small>
                  {invoice?.invoiceRecipient?.client?.id ??
                    invoice?.invoiceRecipient?.clientIdentifier}
                </Small>
              </Col>
              <Col lg={4}>
                <BodySmall color={theme.colors.textSecondary}>
                  Fakturanummer
                </BodySmall>
                <Body>{invoice?.invoiceNumber}</Body>
              </Col>
            </Row>
            <HorizontalSpacer spacing={1} />
            <Row>
              <Col lg={4}>
                <BodySmall color={theme.colors.textSecondary}>Belopp</BodySmall>
                <Body>
                  {formatPrice(invoice?.totalAmountWithVat)}{" "}
                  {invoice?.currencyCode}
                </Body>
              </Col>
              <Col lg={4}>
                <BodySmall color={theme.colors.textSecondary}>
                  Förfallodatum
                </BodySmall>
                <Body>{dayjs(invoice?.dueDate).format("YYYY-MM-DD")}</Body>
              </Col>
              <Col lg={4}>
                <BodySmall color={theme.colors.textSecondary}>
                  Fakturans status
                </BodySmall>
                <Body>
                  {getInvoiceLedgerStateBadge(invoice?.ledgerInvoice?.state)}
                </Body>
              </Col>
            </Row>

            <HorizontalSpacer spacing={1} />
            <Row gutterWidth={16}>
              <Col lg={5}>
                <GreyOutlinedContainer>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Subheading color={theme.colors.primary}>
                      Fakturainfo
                    </Subheading>
                    {invoice?.id && (
                      <CopyForDebuggingButton
                        copyValue={invoice?.id}
                        textValue="Fakturaid"
                        variant="discrete"
                      />
                    )}
                  </div>

                  <DataRow>
                    <BodySmall fontweight={500}>Fakturanummer</BodySmall>
                    <BodySmall>{invoice?.invoiceNumber}</BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Belopp</BodySmall>
                    <BodySmall>
                      {formatPrice(invoice?.totalAmountWithVat)}{" "}
                      {invoice?.currencyCode}
                    </BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Villkor</BodySmall>
                    <BodySmall>
                      {dayjs(invoice?.dueDate).diff(
                        dayjs(invoice?.invoiceDate),
                        "days",
                      )}{" "}
                      dagar
                    </BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Fakturadatum</BodySmall>
                    <BodySmall>
                      {dayjs(invoice?.invoiceDate).format("YYYY-MM-DD")}
                    </BodySmall>
                  </DataRow>
                  <DataRow border>
                    <BodySmall fontweight={500}>Förfallodatum</BodySmall>
                    <BodySmall>
                      {dayjs(invoice?.dueDate).format("YYYY-MM-DD")}
                    </BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Levereras via</BodySmall>
                    <BodySmall>
                      {t(
                        getInvoiceDistributionTypeTranslationKey(
                          invoice?.invoiceRecipient?.distributionType,
                        ),
                      )}
                    </BodySmall>
                  </DataRow>
                  <DataRow border>
                    <BodySmall fontweight={500}>Mottagare</BodySmall>
                    <BodySmall>
                      {t(
                        getInvoiceDistrubitionTypeRecipientAddress(
                          invoice?.invoiceRecipient?.distributionType,
                          invoice?.invoiceRecipient?.ediAddress,
                          invoice?.invoiceRecipient?.recipientEmail,
                          {
                            street: invoice?.invoiceRecipient?.address1,
                            city: invoice?.invoiceRecipient?.city,
                            postalCode: invoice?.invoiceRecipient?.zipCode,
                            country: invoice?.invoiceRecipient?.countryCode,
                          },
                        ),
                      )}
                    </BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Källa</BodySmall>
                    <BodySmall>
                      {t(
                        getDocumentUploadSource(
                          invoice?.uploadDocument?.invoiceUploadUnit
                            ?.uploadType,
                        ),
                      )}
                    </BodySmall>
                  </DataRow>
                  <DataRow border>
                    <BodySmall fontweight={500}>
                      {t("invoices.template")}
                    </BodySmall>
                    <TextLinkButton
                      title={invoice?.template?.name ?? ""}
                      onClick={() => {
                        navigate(
                          `/client/${clientIdentifier}/invoices/templates/edit/${invoice?.template?.id}`,
                        );
                      }}
                    />
                  </DataRow>
                  <DataRow border>
                    <BodySmall fontweight={500}>Tjänst</BodySmall>
                    <BodySmall>
                      {t(
                        getInvoiceServiceTypeTranslationKey(
                          invoice?.serviceType ?? "UNKNOWN",
                        ),
                      )}
                    </BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Provision</BodySmall>
                    <BodySmall>SAKNAS</BodySmall>
                  </DataRow>
                  <DataRow>
                    <BodySmall fontweight={500}>Admin-avgift</BodySmall>
                    <BodySmall>SAKNAS</BodySmall>
                  </DataRow>
                </GreyOutlinedContainer>

                {invoice?.rejectionComments?.items &&
                  invoice?.rejectionComments?.items?.length > 0 && (
                    <div>
                      <HorizontalSpacer spacing={2} />
                      <GreyOutlinedContainer>
                        <Subheading color={theme.colors.error}>
                          Fakturan avvisades
                        </Subheading>
                        {invoice?.rejectionComments?.items.map((comment) => (
                          <div key={comment.id}>
                            <BodySmall color={theme.colors.textSecondary}>
                              {dayjs(comment.createdAt).format(
                                "YYYY-MM-DD HH:mm",
                              )}{" "}
                              - {comment.createdByUser?.firstName}{" "}
                              {comment.createdByUser?.lastName}
                            </BodySmall>
                            <BodySmall>"{comment.content}"</BodySmall>
                          </div>
                        ))}
                      </GreyOutlinedContainer>
                    </div>
                  )}
              </Col>
              <Col lg={7}>
                <GreyOutlinedContainer>
                  <ListTabBar
                    tabs={tabs}
                    selectedId={selectedTabId}
                    onSelectTabId={setSelectedTabId}
                  />
                  {selectedTabId === "done" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <DataRow>
                        <BodySmall fontweight={500}>Källa</BodySmall>
                        <BodySmall>
                          {t(
                            getDocumentUploadSource(
                              invoice?.uploadDocument?.invoiceUploadUnit
                                ?.uploadType,
                            ),
                          )}
                        </BodySmall>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>Registrerad</BodySmall>
                        <BodySmall>
                          {invoice?.uploadDocument?.invoiceUploadUnit
                            ?.statusHistory?.[0]?.setAt
                            ? dayjs(
                                invoice?.uploadDocument?.invoiceUploadUnit
                                  ?.statusHistory?.[0]?.setAt,
                              ).format("YYYY-MM-DD HH:mm:ss")
                            : "Okänt"}
                        </BodySmall>
                      </DataRow>
                      <DataRow border>
                        <BodySmall fontweight={500}>Registrerad av</BodySmall>
                        <BodySmall>
                          {formatUser(
                            invoice?.uploadDocument?.invoiceUploadUnit
                              ?.createdByUser ?? invoice?.registeredByUser,
                          )}
                        </BodySmall>
                      </DataRow>
                      {invoice?.latestValidation && (
                        <>
                          <DataRow>
                            <BodySmall fontweight={500}>Validering</BodySmall>
                            <BodySmall>
                              {invoice?.latestValidation?.isValid
                                ? "Godkänd"
                                : invoice?.latestValidation?.isValid === false
                                  ? "Ej godkänd"
                                  : "Okänd"}
                            </BodySmall>
                          </DataRow>
                          <DataRow>
                            <BodySmall fontweight={500}>Validerades</BodySmall>
                            <BodySmall>
                              {invoice?.latestValidation?.createdAt
                                ? dayjs(
                                    invoice.latestValidation.createdAt,
                                  ).format("YYYY-MM-DD HH:mm:ss")
                                : "Okänt"}
                            </BodySmall>
                          </DataRow>
                          <DataRow border>
                            <BodySmall fontweight={500}>
                              Validerades av
                            </BodySmall>
                            <BodySmall>
                              {formatUser(invoice?.validatedByUser)}
                            </BodySmall>
                          </DataRow>
                        </>
                      )}

                      {invoice?.latestAssessment && (
                        <>
                          <DataRow>
                            <BodySmall fontweight={500}>Bedömning</BodySmall>
                            <BodySmall>
                              {invoice?.state?.assessmentStatus ===
                              "FORCE_APPROVED"
                                ? "Godkänd med fel"
                                : invoice?.state?.assessmentStatus ===
                                    "APPROVED"
                                  ? "Godkänd"
                                  : "Okänd"}
                            </BodySmall>
                          </DataRow>
                          <DataRow>
                            <BodySmall fontweight={500}>Bedömdes</BodySmall>
                            <BodySmall>
                              {invoice?.latestAssessment?.createdAt
                                ? dayjs(
                                    invoice.latestAssessment.createdAt,
                                  ).format("YYYY-MM-DD HH:mm:ss")
                                : "Okänt"}
                            </BodySmall>
                          </DataRow>
                          <DataRow>
                            <BodySmall fontweight={500}>Bedömdes av</BodySmall>
                            <BodySmall>
                              {formatUser(invoice?.assessedByUser)}
                            </BodySmall>
                          </DataRow>
                          <DataRow border>
                            <BodySmall fontweight={500}>Beslut</BodySmall>
                            <BodySmall>
                              {invoice?.latestAssessment?.decision || "Okänt"}
                            </BodySmall>
                          </DataRow>
                        </>
                      )}
                      <DataRow border>
                        <div>
                          {invoiceDeliveryApprovals?.recipientApprovals
                            ?.filter((x) => x.status === "APPROVED")
                            .map((lg) => (
                              <div>
                                <Row nogutter justify="between" align="center">
                                  <BodySmall fontweight={500}>
                                    LG Godkänt{" "}
                                  </BodySmall>
                                  <BodySmall>
                                    {dayjs(lg.changedAt).format("YYYY-MM-DD")}
                                  </BodySmall>
                                </Row>
                                <LgHistoryDetailBlock key={lg.id} lg={lg} />
                              </div>
                            ))}
                          {invoiceDeliveryApprovals?.recipientApprovals
                            ?.length === 0 && (
                            <BodySmall>Inget leveransgodkännande</BodySmall>
                          )}
                        </div>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>
                          Fakturan skickades till mottagare
                        </BodySmall>
                        <BodySmall>
                          {invoice?.ledgerInvoice?.distributedAt
                            ? dayjs(
                                invoice?.ledgerInvoice?.distributedAt,
                              ).format("YYYY-MM-DD")
                            : "-"}
                        </BodySmall>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>Skickades via</BodySmall>
                        <BodySmall>
                          {invoice?.ledgerInvoice?.distributionMethod ??
                            invoice?.ledgerInvoice
                              ?.externalDistributionMethod ??
                            "-"}
                        </BodySmall>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>Förfallostatus</BodySmall>
                        <BodySmall>
                          {getInvoiceOverdueStatusBadge(
                            invoice?.ledgerInvoice?.overdueStatus,
                            true,
                          )}
                        </BodySmall>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>
                          Betalstatus mottagare
                        </BodySmall>
                        <BodySmall>
                          {t(
                            `ledgerInvoice.${invoice?.ledgerInvoice?.paymentStatusRecipient}`,
                          )}
                        </BodySmall>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>
                          Betalstatus utställare
                        </BodySmall>
                        <BodySmall>
                          {t(
                            `ledgerInvoice.${invoice?.ledgerInvoice?.paymentStatusIssuer}`,
                          )}
                        </BodySmall>
                      </DataRow>
                      <DataRow>
                        <BodySmall fontweight={500}>Regress status</BodySmall>
                        <BodySmall>
                          {t(
                            `ledgerInvoice.${invoice?.ledgerInvoice?.recourseStatus}`,
                          )}
                        </BodySmall>
                      </DataRow>
                    </div>
                  )}

                  {selectedTabId === "assessment" && (
                    <div>
                      {invoice?.latestAssessment?.rules ? (
                        <T800Result
                          rules={invoice?.latestAssessment?.rules ?? []}
                          ruleOverrides={
                            invoice?.latestAssessment?.ruleOverrides ?? []
                          }
                        />
                      ) : (
                        <Body>Ingen bedömning tillgänglig</Body>
                      )}
                    </div>
                  )}

                  {selectedTabId === "validation" && (
                    <div>
                      {invoice?.latestValidation ? (
                        <InvoiceValidationResult
                          fieldValidations={
                            invoice?.latestValidation?.fieldValidations
                          }
                          rowFieldValidations={
                            invoice?.latestValidation?.rowValidations
                          }
                          rules={
                            invoice?.latestValidation?.ruleValidations?.rules
                          }
                        />
                      ) : (
                        <Body>Ingen validering tillgänglig</Body>
                      )}
                    </div>
                  )}
                </GreyOutlinedContainer>
              </Col>
            </Row>
          </Col>
          {
            <Col lg={5}>
              <InvoiceViewColumn
                fileId={invoice?.invoiceFileId || ""}
                collapsible
              />
            </Col>
          }
        </Row>

        <HorizontalSpacer spacing={2} />
        <Subheading color={theme.colors.primary}>Dokument & filer</Subheading>
        <HorizontalSpacer spacing={1} />
        <div style={{ display: "flex", gap: 16 }}>
          <GreyOutlinedContainer>
            <BodySmall color={theme.colors.textSecondary}>Avräkning</BodySmall>
            <BodySmall color={theme.colors.textMuted}>2026-01-01</BodySmall>
            <DownloadButton title="Avräkning fil.pdf" />
          </GreyOutlinedContainer>
          <GreyOutlinedContainer>
            <BodySmall color={theme.colors.textSecondary}>Betalning</BodySmall>
            <BodySmall color={theme.colors.textMuted}>2026-01-01</BodySmall>
            <DownloadButton title="payment.pdf" />
          </GreyOutlinedContainer>
        </div>
        <HorizontalSpacer spacing={1} />
        <div style={{ display: "flex", gap: 16 }}>
          <GreyOutlinedContainer>
            <BodySmall color={theme.colors.textSecondary}>Påminnelse</BodySmall>
            <BodySmall color={theme.colors.textMuted}>2026-01-01</BodySmall>
            <DownloadButton title="reminder.pdf" />
          </GreyOutlinedContainer>
          <GreyOutlinedContainer>
            <BodySmall color={theme.colors.textSecondary}>
              Inkassokrav
            </BodySmall>
            <BodySmall color={theme.colors.textMuted}>2026-01-01</BodySmall>
            <DownloadButton title="collection.pdf" />
          </GreyOutlinedContainer>
          <GreyOutlinedContainer>
            <BodySmall color={theme.colors.textSecondary}>
              Räntefaktura
            </BodySmall>
            <BodySmall color={theme.colors.textMuted}>2026-01-01</BodySmall>
            <DownloadButton title="interest_invoice.pdf" />
          </GreyOutlinedContainer>
        </div>
      </Card>
    </div>
  );
};

export default InvoiceOverviewPage;
